import React, { useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
      CCard,
      CCardBody,
      CButton,
      CRow,
      CCol,
      CCardImage,
      CModal,
      CModalHeader,
      CModalTitle,
      CModalBody,
      CModalFooter,
      CBadge,
      CAlert
} from '@coreui/react';
import { AuthUserContext } from '../../session';
import { useCart } from '../../../context/CartContext';
import { toast } from 'react-toastify';
import { ActiveLinkContext } from '../../../components/ActiveLinkContext';
import StripeCheckoutModal from '../../../components/StripeCheckoutModal';
import MierosTransferModal from '../../../components/MierosTransferModal';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, push, query, orderByChild, equalTo, get } from 'firebase/database';
import Logger from '../../../utils/Logger';
import PaymentStatusModal from '../../../components/PaymentStatusModal';
import config from '../../../utils/EnvironmentSwitcher';
import { fetchWithFabricRetry } from '../../../utils/fabricReEnroll';
import { categorizeCartItems, getPaymentSummary, getUniqueSellers } from '../../../utils/paymentUtils';

const CartItem = ({ item, onRemove }) => {
      const price = Number(item.price) || 0;
      const mierosPrice = Number(item.mierosPrice) || 0;
      const isFree = price === 0 && mierosPrice === 0;
      const isMierosOnly = price === 0 && mierosPrice > 0;
      const isMixed = price > 0 && mierosPrice > 0;
      
      return (
            <CCard className="mb-3">
                  <CCardBody>
                        <CRow>
                              <CCol md={3}>
                                    <CCardImage
                                          src={`https://ipfs.mieros.cloud/ipfs/${item.metadata?.image}`}
                                          style={{ maxHeight: '100px', objectFit: 'contain' }}
                                    />
                              </CCol>
                              <CCol md={6}>
                                    <h5>{item.itemDetail || item.metadata?.title}</h5>
                                    <p className="mb-2">
                                          {item.itemMedia} • {item.itemCategory}
                                    </p>
                                    {isMierosOnly && (
                                          <CBadge color="info" className="mt-1">MiEROS Tokens Only</CBadge>
                                    )}
                                    {isMixed && (
                                          <CBadge color="warning" className="mt-1">Mixed Payment</CBadge>
                                    )}
                              </CCol>
                              <CCol md={3} className="text-end">
                                    {isFree ? (
                                          <div className="fw-bold mb-2 text-success">Free</div>
                                    ) : isMierosOnly ? (
                                          <div className="fw-bold mb-2">{mierosPrice} MiEROS</div>
                                    ) : isMixed ? (
                                          <div className="fw-bold mb-2">
                                                ${price.toFixed(2)} USD
                                                <br />
                                                <small className="mb-2">+ {mierosPrice} MiEROS</small>
                                          </div>
                                    ) : (
                                          <div className="fw-bold mb-2">${price.toFixed(2)}</div>
                                    )}
                                    <CButton
                                          color="danger"
                                          variant="ghost"
                                          size="sm"
                                          onClick={onRemove}
                                    >
                                          Remove
                                    </CButton>
                              </CCol>
                        </CRow>
                  </CCardBody>
            </CCard>
      );
};

const CartPage = () => {
      const { 
            cartItems, 
            removeFromCart, 
            getCartTotal, 
            clearCart,
            hasItems 
      } = useCart();
      const [isProcessing, setIsProcessing] = useState(false);
      const [showStripeModal, setShowStripeModal] = useState(false);
      const [showMierosModal, setShowMierosModal] = useState(false);
      const [clientSecret, setClientSecret] = useState(null);
      const [stripeAccountId, setStripeAccountId] = useState(null);
      const { setActiveLink } = useContext(ActiveLinkContext);
      const { authUser } = useContext(AuthUserContext);
      const navigate = useNavigate();
      const [paymentStatus, setPaymentStatus] = useState(null);
      const [showPaymentModal, setShowPaymentModal] = useState(false);
      const [mierosTransferData, setMierosTransferData] = useState(null);

      // Analyze cart items to determine payment type
      const paymentAnalysis = useMemo(() => {
            if (!cartItems || cartItems.length === 0) return null;
            const categorized = categorizeCartItems(cartItems);
            const summary = getPaymentSummary(categorized);
            return { ...categorized, summary };
      }, [cartItems]);

      const handleCloseStripeModal = () => {
            setShowStripeModal(false);
            setClientSecret(null);
      };

      const createCheckoutSession = async (cartItems) => {
            try {
                  // Validate cart items
                  if (!cartItems || cartItems.length === 0) {
                        throw new Error('No items to checkout');
                  }

                  const headers = {
                        'Content-Type': 'application/json'
                  };

                  if (authUser) {
                        headers['Authorization'] = `Bearer ${authUser.stsTokenManager.accessToken}`;
                  }

                  // Get the first item and validate it has required properties
                  const firstItem = cartItems[0];
                  if (!firstItem) {
                        throw new Error('Invalid cart item');
                  }

                  // Get price - check multiple possible locations
                  const price = firstItem.price || firstItem.itemPrice || firstItem.metadata?.price || 0;
                  
                  if (!price || price <= 0) {
                        throw new Error('Invalid price for checkout session');
                  }

                  const priceInCents = Math.round(parseFloat(price) * 100);

                  Logger.debug('Price calculation:', {
                        originalPrice: price,
                        parsedPrice: parseFloat(price),
                        finalPriceInCents: priceInCents,
                        item: firstItem
                  });

                  const requestBody = {
                        priceData: price,
                        productId: firstItem.nftId || firstItem.nftID,
                        metadata: {
                              nftId: firstItem.nftId || firstItem.nftID,
                              listingId: firstItem.listingID || firstItem.listingId,
                              itemMedia: firstItem.itemMedia || firstItem.media,
                              itemCategory: firstItem.itemCategory || firstItem.category,
                              sellerID: firstItem.sellerID || firstItem.sellerId,
                              docType: firstItem.docType,
                              version: firstItem.version,
                              price: price
                        }
                  };

                  console.log('Request body:', JSON.stringify(requestBody, null, 2));

                  const response = await fetch(`${config.paymentUrl}/create-checkout-session`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(requestBody)
                  });

                  const text = await response.text();
                  let data;
                  try {
                        data = text ? JSON.parse(text) : {};
                  } catch {
                        data = {};
                  }

                  if (!response.ok) {
                        const message = data.error || data.message || (response.status === 502
                              ? 'Payment service temporarily unavailable (502). Please try again later.'
                              : response.status === 503
                                    ? 'Payment service temporarily unavailable. Please try again later.'
                                    : `Failed to create checkout session (${response.status})`);
                        throw new Error(message);
                  }

                  return data;
            } catch (error) {
                  Logger.error('Error creating checkout session:', error);
                  console.error('Full error details:', error);
                  throw error;
            }
      };

      const handleCheckout = async () => {
            if (!authUser) {
                  toast.error('Please log in to proceed with checkout');
                  return;
            }

            if (!paymentAnalysis) {
                  toast.error('No items in cart');
                  return;
            }

            setIsProcessing(true);

            try {
                  const { summary, mierosOnlyItems, usdOnlyItems, mixedItems, mierosTotal } = paymentAnalysis;

                  Logger.debug('Checkout analysis:', {
                        summary,
                        mierosOnlyCount: mierosOnlyItems.length,
                        usdOnlyCount: usdOnlyItems.length,
                        mixedCount: mixedItems.length,
                        totalCartItems: cartItems.length
                  });

                  // Scenario 1: Mieros-only (price = 0)
                  if (summary.type === 'mieros') {
                        // Get seller ID from first item (assuming single seller for now)
                        const sellerId = mierosOnlyItems[0]?.sellerID || mierosOnlyItems[0]?.sellerId;
                        
                        if (!sellerId) {
                              throw new Error('Seller ID not found for Mieros-only items');
                        }

                        // If amount is 0 (free item), skip transfer modal and go straight to NFT transfer
                        if (mierosTotal === 0) {
                              Logger.debug('Free Mieros-only item, skipping transfer modal and going straight to NFT transfer');
                              
                              // Set transfer data for NFT transfer
                              const freeTransferData = {
                                    recipientId: sellerId,
                                    amount: 0,
                                    items: mierosOnlyItems
                              };
                              
                              // Complete the purchase directly (no token transfer needed)
                              await completeMierosOnlyPurchase(freeTransferData);
                              setIsProcessing(false);
                              return;
                        }

                        // Prepare transfer data for items with Mieros amount > 0
                        setMierosTransferData({
                              recipientId: sellerId,
                              amount: mierosTotal,
                              items: mierosOnlyItems
                        });
                        setShowMierosModal(true);
                        setIsProcessing(false);
                        return;
                  }

                  // Scenario 2: USD-only (price > 0, no Mieros)
                  if (summary.type === 'usd') {
                        const itemsToCheckout = [...usdOnlyItems, ...mixedItems];
                        
                        Logger.debug('USD checkout items:', {
                              usdOnlyItems: usdOnlyItems.length,
                              mixedItems: mixedItems.length,
                              itemsToCheckout: itemsToCheckout.length,
                              firstItem: itemsToCheckout[0]
                        });
                        
                        // Validate we have items with valid prices
                        if (itemsToCheckout.length === 0) {
                              throw new Error('No items with USD payment found');
                        }
                        
                        // Validate all items have prices
                        const itemsWithPrices = itemsToCheckout.filter(item => {
                              const price = Number(item.price) || Number(item.itemPrice) || Number(item.metadata?.price) || 0;
                              Logger.debug('Item price check:', { item, price, hasPrice: price > 0 });
                              return price > 0;
                        });
                        
                        if (itemsWithPrices.length === 0) {
                              Logger.error('No items with valid prices:', itemsToCheckout);
                              throw new Error('No items with valid USD prices found. Please check item prices.');
                        }
                        
                        Logger.debug('Creating checkout session with items:', itemsWithPrices);
                        const response = await createCheckoutSession(itemsWithPrices);

                        if (response.clientSecret) {
                              setClientSecret(response.clientSecret);
                              setStripeAccountId(response.stripeAccountId);
                              setShowStripeModal(true);
                        } else {
                              throw new Error('Failed to create checkout session');
                        }
                        setIsProcessing(false);
                        return;
                  }

                  // Scenario 3: Mixed (both Mieros + USD)
                  if (summary.type === 'mixed') {
                        // First, handle Mieros transfer
                        const mierosItems = [...mierosOnlyItems, ...mixedItems];
                        const sellerId = mierosItems[0]?.sellerID || mierosItems[0]?.sellerId;
                        
                        if (!sellerId) {
                              throw new Error('Seller ID not found for Mieros payment');
                        }

                        // Show Mieros transfer modal first
                        setMierosTransferData({
                              recipientId: sellerId,
                              amount: mierosTotal,
                              items: mierosItems,
                              onComplete: async () => {
                                    // After Mieros transfer completes, proceed to Stripe
                                    const usdItems = [...usdOnlyItems, ...mixedItems];
                                    const response = await createCheckoutSession(usdItems);
                                    
                                    if (response.clientSecret) {
                                          setClientSecret(response.clientSecret);
                                          setStripeAccountId(response.stripeAccountId);
                                          setShowStripeModal(true);
                                    } else {
                                          throw new Error('Failed to create checkout session');
                                    }
                              }
                        });
                        setShowMierosModal(true);
                        setIsProcessing(false);
                        return;
                  }

            } catch (error) {
                  console.error('Checkout error:', error);
                  toast.error(error.message || 'Failed to initiate checkout');
                  setIsProcessing(false);
            }
      };

      const handleMierosTransferSuccess = async (transferData) => {
            Logger.info('Mieros transfer successful:', transferData);
            
            // If there's an onComplete callback (mixed payment), execute it
            if (mierosTransferData?.onComplete) {
                  await mierosTransferData.onComplete();
            } else {
                  // Mieros-only payment: complete the purchase with transfer data
                  // Merge the transferData from modal (which has the actual transferred amount) 
                  // with the existing mierosTransferData (which has the items)
                  // IMPORTANT: transferData.amount is the actual amount that was transferred
                  const mergedTransferData = {
                        ...mierosTransferData,
                        items: mierosTransferData?.items || [],
                        // Override with transferData values (amount, transactionId, recipientId from actual transfer)
                        amount: transferData.amount, // Use the actual transferred amount
                        transactionId: transferData.transactionId || mierosTransferData?.transactionId,
                        recipientId: transferData.recipientId || mierosTransferData?.recipientId,
                        timestamp: transferData.timestamp || mierosTransferData?.timestamp
                  };
                  
                  Logger.debug('Merged transfer data for purchase record:', {
                        originalAmount: mierosTransferData?.amount,
                        transferredAmount: transferData.amount,
                        mergedAmount: mergedTransferData.amount,
                        recipientId: mergedTransferData.recipientId
                  });
                  
                  await completeMierosOnlyPurchase(mergedTransferData);
            }
            
            setShowMierosModal(false);
            setMierosTransferData(null);
      };

      const completeMierosOnlyPurchase = async (transferData = null) => {
            // Use provided transferData or fall back to state
            const dataToUse = transferData || mierosTransferData;
            
            if (!authUser || !dataToUse) {
                  Logger.error('Cannot complete purchase: missing authUser or transferData');
                  return;
            }

            try {
                  // Get Firebase token - authUser from context is not a Firebase user object
                  const auth = getAuth();
                  const currentUser = auth.currentUser;
                  
                  if (!currentUser) {
                        throw new Error('No authenticated user found');
                  }
                  
                  const idToken = await currentUser.getIdToken();
                  
                  // Transfer NFTs sequentially to avoid MVCC_READ_CONFLICT on shared balances
                  const transferResults = [];
                  for (const item of dataToUse.items) {
                        const response = await fetchWithFabricRetry(`${config.apiUrl}/nft/transfer`, {
                              method: 'POST',
                              headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${idToken}`
                              },
                              body: JSON.stringify({
                                    nftId: item.nftId || item.nftID,
                                    listingId: item.listingID || item.listingId,
                                    price: 0, // Mieros-only, no USD
                                    sellerId: item.sellerID || item.sellerId,
                                    buyerId: currentUser.uid,
                                    mierosAmount: dataToUse.amount || 0,
                                    transactionId: dataToUse.transactionId || null
                              })
                        });

                        if (!response.ok) {
                              const errorData = await response.json();
                              throw new Error(errorData.message || 'Failed to transfer NFT');
                        }

                        transferResults.push(await response.json());
                  }
                  
                  // Create purchase record in Firebase
                  const db = getDatabase();
                  const purchaseHistoryRef = ref(db, 'purchase_history');
                  
                  // Build line items for purchase record
                  const lineItems = dataToUse.items.map((item, index) => ({
                        product_name: item.itemDetail || item.metadata?.title || 'NFT',
                        product_description: item.itemDescription || item.metadata?.description || '',
                        price: 0, // Price in cents (0 for Mieros-only)
                        mierosPrice: Number(item.mierosPrice) || 0, // Store MiEROS price for reference
                        quantity: 1,
                        product_metadata: {
                              nftId: item.nftId || item.nftID,
                              listingId: item.listingID || item.listingId,
                              sellerId: item.sellerID || item.sellerId,
                              itemMedia: item.itemMedia || item.media,
                              itemCategory: item.itemCategory || item.category,
                              mierosPrice: Number(item.mierosPrice) || 0 // Also store in metadata
                        }
                  }));

                  // Get seller/recipient ID from transfer data or first item
                  const sellerId = dataToUse.recipientId || dataToUse.items[0]?.sellerID || dataToUse.items[0]?.sellerId;
                  
                  // Get the actual transferred amount (from transferData callback, which has the real amount)
                  // This is important because the modal callback provides the actual amount that was transferred
                  let actualMierosAmount = dataToUse.amount || 0;
                  
                  // Fallback: If amount is 0 or missing, calculate from items' mierosPrice
                  if (actualMierosAmount === 0 && dataToUse.items && dataToUse.items.length > 0) {
                        const calculatedAmount = dataToUse.items.reduce((sum, item) => {
                              const itemMierosPrice = Number(item.mierosPrice) || 0;
                              return sum + itemMierosPrice;
                        }, 0);
                        
                        if (calculatedAmount > 0) {
                              Logger.warn('MiEROS amount was 0, calculating from items:', {
                                    calculatedAmount,
                                    items: dataToUse.items.map(item => ({
                                          nftId: item.nftId,
                                          mierosPrice: item.mierosPrice
                                    }))
                              });
                              actualMierosAmount = calculatedAmount;
                        }
                  }
                  
                  // Generate a unique order number for this purchase
                  // Format: ORD-{timestamp}-{random}-{userIdPrefix}
                  const timestamp = Date.now();
                  const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
                  const userIdPrefix = currentUser.uid.substring(0, 4).toUpperCase();
                  const orderNumber = `ORD-${timestamp}-${randomStr}-${userIdPrefix}`;
                  
                  Logger.debug('Creating purchase record:', {
                        sellerId,
                        actualMierosAmount,
                        dataToUseAmount: dataToUse.amount,
                        recipientId: dataToUse.recipientId,
                        itemsCount: dataToUse.items?.length || 0,
                        itemsMierosPrices: dataToUse.items?.map(item => item.mierosPrice) || [],
                        orderNumber
                  });

                  const purchaseRecord = {
                        email: currentUser.email || authUser.email,
                        status: 'complete',
                        payment_status: 'paid',
                        payment_method: 'mieros_tokens',
                        amount_total: 0, // USD amount (0 for Mieros-only)
                        mieros_amount: actualMierosAmount, // Use the actual transferred amount
                        currency: 'mieros',
                        timestamp: timestamp,
                        orderNumber: orderNumber, // Unique order number for this purchase
                        line_items: lineItems,
                        buyerId: currentUser.uid,
                        sellerId: sellerId, // Add seller/recipient ID
                        recipientId: dataToUse.recipientId || sellerId, // Also store recipientId for clarity
                        transactionId: dataToUse.transactionId || `mieros_${timestamp}_${Math.random().toString(36).substring(2, 9)}`,
                        transferResults: transferResults
                  };

                  // Try to save purchase record to Firebase (may fail due to permissions, but purchase is already complete)
                  try {
                        // Check if an order with this orderNumber already exists (primary check)
                        const existingOrderNumberQuery = query(
                              purchaseHistoryRef,
                              orderByChild('orderNumber'),
                              equalTo(purchaseRecord.orderNumber)
                        );
                        const existingOrderNumberSnapshot = await get(existingOrderNumberQuery);
                        
                        if (existingOrderNumberSnapshot.exists()) {
                              Logger.warn('Order with this orderNumber already exists, skipping save:', {
                                    orderNumber: purchaseRecord.orderNumber,
                                    existingOrder: Object.values(existingOrderNumberSnapshot.val())[0]
                              });
                        } else {
                              // Also check by transactionId as a secondary check (for older orders)
                              const existingTransactionQuery = query(
                                    purchaseHistoryRef,
                                    orderByChild('transactionId'),
                                    equalTo(purchaseRecord.transactionId)
                              );
                              const existingTransactionSnapshot = await get(existingTransactionQuery);
                              
                              if (existingTransactionSnapshot.exists()) {
                                    Logger.warn('Order with this transactionId already exists, skipping save:', {
                                          transactionId: purchaseRecord.transactionId,
                                          orderNumber: purchaseRecord.orderNumber,
                                          existingOrder: Object.values(existingTransactionSnapshot.val())[0]
                                    });
                              } else {
                                    await push(purchaseHistoryRef, purchaseRecord);
                                    // Index by buyerId so Orders page can query reliably after MiEROS-only checkout
                                    await push(ref(db, `user_purchases/${currentUser.uid}`), purchaseRecord);
                                    Logger.info('Purchase record created in Firebase:', {
                                          orderNumber: purchaseRecord.orderNumber,
                                          transactionId: purchaseRecord.transactionId
                                    });
                              }
                        }
                  } catch (firebaseError) {
                        // Log warning but don't fail the purchase - the NFT transfer already succeeded
                        Logger.warn('Could not save purchase record to Firebase (purchase still completed):', firebaseError);
                        // Store in localStorage as backup
                        try {
                              const localPurchases = JSON.parse(localStorage.getItem('local_purchases') || '[]');
                              localPurchases.push({
                                    ...purchaseRecord,
                                    savedLocally: true,
                                    savedAt: Date.now()
                              });
                              localStorage.setItem('local_purchases', JSON.stringify(localPurchases));
                              Logger.info('Purchase record saved to localStorage as backup');
                        } catch (localError) {
                              Logger.warn('Could not save to localStorage either:', localError);
                        }
                  }
                  
                  // Show success message and confirmation
                  toast.success('Purchase completed successfully!');
                  
                  // Set payment status for confirmation modal
                  setPaymentStatus({
                        status: 'complete',
                        payment_status: 'paid',
                        customer_email: currentUser.email || authUser.email,
                        message: `Your purchase has been completed successfully! ${actualMierosAmount > 0 ? `${actualMierosAmount} MiEROS tokens were transferred.` : 'Free item - no payment required.'}`,
                        mierosAmount: actualMierosAmount,
                        itemsCount: dataToUse.items.length
                  });
                  setShowPaymentModal(true);
                  
                  clearCart();
                  
                  // Navigate to orders after a short delay (optional)
                  // navigate('/orders');
            } catch (error) {
                  Logger.error('Error completing Mieros-only purchase:', error);
                  toast.error(`Purchase failed: ${error.message}`);
            }
      };

      const handlePaymentComplete = (status) => {
            setPaymentStatus(status);
            setShowPaymentModal(true);
            if (status.status === 'complete' && status.payment_status === 'paid') {
                  clearCart();
            }
      };

      // Also log cart items when they're displayed
      const renderCartItems = () => {
            return cartItems.map(item => {
                  console.log('Rendering cart item:', item);
                  return (
                        <CartItem
                              key={item.nftId}
                              item={item}
                              onRemove={() => removeFromCart(item.nftId)}
                        />
                  );
            });
      };

      if (!hasItems()) {
            return (
                  <div className="cart-page p-4">
                        <h2>Your Cart</h2>
                        <div className="text-center py-5">
                              <h4>Your cart is empty</h4>
                              <CButton
                                    color="primary"
                                    onClick={() => navigate('/marketplace')}
                                    className="mt-3"
                              >
                                    Continue Shopping
                              </CButton>
                        </div>
                  </div>
            );
      }

      return (
            <div className="cart-page p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2>Your Cart {!authUser && <span className="mb-2">(Guest)</span>}</h2>
                        <CButton
                              color="secondary"
                              variant="outline"
                              onClick={() => navigate('/orders')}
                        >
                              View Orders
                        </CButton>
                  </div>
                  {cartItems.length === 0 ? (
                        <div className="text-center py-5">
                              <h4>Your cart is empty</h4>
                              <CButton
                                    color="primary"
                                    onClick={() => navigate('/marketplace')}
                                    className="mt-3"
                              >
                                    Continue Shopping
                              </CButton>
                              <CButton
                                    color="primary"
                                    className="mt-3"
                                    onClick={() => navigate('/orders')}
                              >
                                    View Orders
                              </CButton>
                        </div>
                  ) : (
                        <>
                              <div className="cart-items mb-4">
                                    {renderCartItems()}
                              </div>

                              <div className="checkout-section">
                                    <CCard>
                                          <CCardBody>
                                                {paymentAnalysis && (
                                                      <CAlert 
                                                            color={paymentAnalysis.summary.type === 'mixed' ? 'warning' : 'info'} 
                                                            className="mb-3"
                                                      >
                                                            <strong>Payment Type:</strong> {paymentAnalysis.summary.description}
                                                            {paymentAnalysis.summary.mierosAmount > 0 && (
                                                                  <div className="mt-2">
                                                                        <strong>MiEROS:</strong> {paymentAnalysis.summary.mierosAmount} tokens
                                                                  </div>
                                                            )}
                                                            {paymentAnalysis.summary.usdAmount > 0 && (
                                                                  <div className="mt-2">
                                                                        <strong>USD:</strong> ${paymentAnalysis.summary.usdAmount.toFixed(2)}
                                                                  </div>
                                                            )}
                                                      </CAlert>
                                                )}
                                                <div className="d-flex justify-content-between mb-3">
                                                      <h4>Total</h4>
                                                      <h4>
                                                            {paymentAnalysis?.summary.type === 'mieros' ? (
                                                                  `${paymentAnalysis.summary.mierosAmount} MiEROS`
                                                            ) : paymentAnalysis?.summary.type === 'mixed' ? (
                                                                  <>
                                                                        ${paymentAnalysis.summary.usdAmount.toFixed(2)} USD
                                                                        <br />
                                                                        <small className="mb-2">
                                                                              + {paymentAnalysis.summary.mierosAmount} MiEROS
                                                                        </small>
                                                                  </>
                                                            ) : (
                                                                  `$${getCartTotal()}`
                                                            )}
                                                      </h4>
                                                </div>
                                                <div className="d-grid gap-2">
                                                      <CButton
                                                            color="primary"
                                                            size="lg"
                                                            onClick={handleCheckout}
                                                            disabled={isProcessing || !authUser}
                                                      >
                                                            {isProcessing ? 'Processing...' : 'Proceed to Checkout'}
                                                      </CButton>
                                                      {!authUser && (
                                                            <CAlert color="warning" className="mt-2">
                                                                  Please log in to proceed with checkout
                                                            </CAlert>
                                                      )}
                                                      <CButton
                                                            color="secondary"
                                                            variant="ghost"
                                                            onClick={() => navigate('/marketplace')}
                                                      >
                                                            Continue Shopping
                                                      </CButton>
                                                      <CButton
                                                            color="primary"
                                                            onClick={() => navigate('/orders')}
                                                      >
                                                            View Orders
                                                      </CButton>
                                                </div>
                                          </CCardBody>
                                    </CCard>
                              </div>
                        </>
                  )}

                  {showStripeModal && clientSecret && (
                        <StripeCheckoutModal
                              show={true}
                              onHide={handleCloseStripeModal}
                              clientSecret={clientSecret}
                              stripeAccountId={stripeAccountId}
                              items={cartItems}
                              setActiveLink={setActiveLink}
                        />
                  )}

                  {showMierosModal && mierosTransferData && (
                        <MierosTransferModal
                              show={showMierosModal}
                              onHide={() => {
                                    setShowMierosModal(false);
                                    setMierosTransferData(null);
                              }}
                              onSuccess={handleMierosTransferSuccess}
                              recipientId={mierosTransferData.recipientId}
                              amount={mierosTransferData.amount}
                              title="Pay with MiEROS Tokens"
                              readOnly={true}
                              showBalance={true}
                        />
                  )}

                  {showPaymentModal && paymentStatus && (
                        <PaymentStatusModal
                              show={showPaymentModal}
                              onHide={() => {
                                    setShowPaymentModal(false);
                                    setPaymentStatus(null);
                              }}
                              onComplete={() => {
                                    setShowPaymentModal(false);
                                    setPaymentStatus(null);
                                    navigate('/orders');
                              }}
                              paymentDetails={paymentStatus}
                        />
                  )}
            </div>
      );
};

export default CartPage;

